import { fillRouteWithSlugs } from "@utils";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetDocumentsWithAllVersions } from "src/hooks/queries";
import { useGlobalDeviceContext } from "src/hooks/useGlobalDeviceContext";
import { ChangeManagementPlan } from "src/types/change-management";
import {
  getDocVersionRevisionNumber,
  hasRevisionNumber,
} from "src/utils/documents";
import { buildDocumentRoute } from "src/utils/navigation";
import { getVersionTile, VersionTile } from "./change-management-plan";

export type ResolvedPlanDocument = { href: string; tile: VersionTile | null };
export type PlanDocumentResolver = (
  documentId: string,
  versionId: string
) => ResolvedPlanDocument | null;

/**
 * The plan only stores document and version IDs, so each document's device
 * and version state are read from the document itself. Organization-wide
 * documents have no device: they open under the current device, or without
 * one when none is selected.
 */
export const useResolvedPlanDocuments = (
  orgId: string,
  plan: ChangeManagementPlan
): PlanDocumentResolver => {
  const documentIds = [...plan.recommendations, ...plan.process_sources]
    .map(({ document_id }) => document_id)
    .filter((documentId): documentId is string => !!documentId);
  const { data: documents } = useGetDocumentsWithAllVersions(
    orgId,
    documentIds
  );
  const { activeGlobalDeviceId } = useGlobalDeviceContext();

  return (documentId, versionId) => {
    const document = documents[documentId];
    if (!document) return null;
    const deviceId = document.deviceId ?? activeGlobalDeviceId;
    const version = document.versions.find(({ id }) => id === versionId);
    return {
      href: deviceId
        ? buildDocumentRoute({ orgId, deviceId, documentId, versionId })
        : `${fillRouteWithSlugs(ROUTES.ORGANIZATION_DOCUMENT, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
            [ROUTE_SLUGS.DOC_ID]: documentId,
          })}?v=${versionId}`,
      tile: version
        ? getVersionTile({
            revision: hasRevisionNumber(version)
              ? getDocVersionRevisionNumber(version, document.versions)
              : null,
            readyForApproval: version.readyForApproval,
          })
        : null,
    };
  };
};
